import { ApiSimpleSubscription } from "@incendium/api";

export type VolumeMetric = "maxOrders" | "maxPageviews" | "maxRevenue";

export const UNLIMITED_VOLUME = -1;

type VolumeFields = Pick<
  ApiSimpleSubscription,
  "maxOrders" | "maxPageviews" | "maxRevenue"
>;

function hasVolumeValue(value?: number): boolean {
  return value != null && value !== 0;
}

/**
 * Pick the volume axis for a set of subscriptions.
 * Business rule: within a merchant set, if any use a metric they all should —
 * so the first present metric wins (orders → pageviews → revenue).
 */
export function resolveVolumeMetric(
  subscriptions: VolumeFields[]
): VolumeMetric | null {
  if (subscriptions.some((s) => hasVolumeValue(s.maxOrders))) {
    return "maxOrders";
  }
  if (subscriptions.some((s) => hasVolumeValue(s.maxPageviews))) {
    return "maxPageviews";
  }
  if (subscriptions.some((s) => hasVolumeValue(s.maxRevenue))) {
    return "maxRevenue";
  }
  return null;
}

export function isUnlimitedVolume(value?: number | null): boolean {
  return value === UNLIMITED_VOLUME;
}

export function getSubscriptionVolumeValue(
  subscription: VolumeFields,
  metric: VolumeMetric
): number | null {
  const value = subscription[metric];
  if (!hasVolumeValue(value)) {
    return null;
  }
  return value as number;
}

/** Sort key so unlimited (-1) is always highest. */
export function getVolumeSortValue(value: number | null): number {
  if (value == null) {
    return 0;
  }
  if (isUnlimitedVolume(value)) {
    return Number.POSITIVE_INFINITY;
  }
  return value;
}

export function formatVolumeMark(value: number): string {
  if (isUnlimitedVolume(value)) {
    return "Unlimited";
  }
  if (value >= 1000) {
    const thousands = value / 1000;
    const label = Number.isInteger(thousands)
      ? `${thousands}k`
      : `${parseFloat(thousands.toFixed(1))}k`;
    return value >= 200000 ? `${label}+` : label;
  }
  return String(value);
}

export function getVolumeMarkLabel(
  subscription: VolumeFields,
  metric: VolumeMetric | null,
  fallback?: string
): string {
  if (metric) {
    const value = getSubscriptionVolumeValue(subscription, metric);
    if (value != null) {
      return formatVolumeMark(value);
    }
  }
  return fallback || "—";
}

export function getVolumeMetricCopy(metric: VolumeMetric | null): {
  overline: string;
  helper: string;
} {
  switch (metric) {
    case "maxPageviews":
      return {
        overline: "Monthly pageviews",
        helper:
          "Adjust the slider to your average monthly pageviews to view your price bracket.",
      };
    case "maxRevenue":
      return {
        overline: "Monthly revenue",
        helper:
          "Adjust the slider to your average monthly revenue to view your price bracket.",
      };
    case "maxOrders":
    default:
      return {
        overline: "Monthly volume",
        helper:
          "Adjust the slider to your average monthly order size to view your price bracket.",
      };
  }
}
